import { listChatbots } from '@/features/chatbot/api';
import { listAccessGroups, listDepartments } from '@/features/configuracoes/api';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { type AutoAssignMode, type WhatsappNumber, updateChannelSettings } from './api';

interface Props {
  channel: WhatsappNumber;
  onClose: () => void;
}

/** Opções do select de inatividade (valor em minutos; 0 = Não fechar). */
const AUTO_CLOSE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Não fechar' },
  { value: 1, label: '1 minuto' },
  { value: 5, label: '5 minutos' },
  { value: 10, label: '10 minutos' },
  { value: 15, label: '15 minutos' },
  { value: 20, label: '20 minutos' },
  { value: 25, label: '25 minutos' },
  { value: 30, label: '30 minutos' },
  { value: 35, label: '35 minutos' },
  { value: 40, label: '40 minutos' },
  { value: 45, label: '45 minutos' },
  { value: 60, label: '1 hora' },
  { value: 120, label: '2 horas' },
  { value: 180, label: '3 horas' },
  { value: 240, label: '4 horas' },
  { value: 300, label: '5 horas' },
  { value: 360, label: '6 horas' },
  { value: 1440, label: '1 dia' },
  { value: 2880, label: '2 dias' },
  { value: 10080, label: '1 semana' },
];

/** Atendente desconectado há X min → os ativos dele neste canal voltam pra Pendentes. */
const OFFLINE_TO_PENDING_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Não travar' },
  { value: 1, label: '1 minuto' },
  { value: 2, label: '2 minutos' },
  { value: 5, label: '5 minutos' },
  { value: 10, label: '10 minutos' },
  { value: 15, label: '15 minutos' },
  { value: 20, label: '20 minutos' },
  { value: 30, label: '30 minutos' },
  { value: 45, label: '45 minutos' },
  { value: 60, label: '1 hora' },
  { value: 120, label: '2 horas' },
  { value: 240, label: '4 horas' },
  { value: 480, label: '8 horas' },
];

/** Valor gravado fora da lista (API/MCP) aparece como está, em vez de o select
 *  mostrar a 1ª opção ("desligado") e esconder o que vale de verdade. */
function opcoesComValorAtual(
  opcoes: { value: number; label: string }[],
  atual: number | null,
): { value: number; label: string }[] {
  if (!atual || opcoes.some((o) => o.value === atual)) return opcoes;
  const label = atual === 1 ? '1 minuto' : `${atual} minutos`;
  return [...opcoes, { value: atual, label }].sort((a, b) => a.value - b.value);
}

/** Opções de distribuição automática de tickets do canal. */
const AUTO_ASSIGN_OPTIONS: { value: AutoAssignMode; label: string }[] = [
  { value: 'off', label: 'Desabilitar distribuição automática' },
  { value: 'balance', label: 'Menos atendimento ativo' },
  { value: 'random', label: 'Aleatório' },
  { value: 'sequential', label: 'Circular' },
];

/**
 * Configurações administrativas de um canal: apelido + chatbot default +
 * departamento default. Substitui o antigo RenameChannelModal — agrupa
 * tudo numa única tela. Admin-only (botão Pencil só aparece pra admin).
 */
export function ChannelGeneralSettingsModal({ channel, onClose }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState(channel.displayName ?? '');
  const [chatbotId, setChatbotId] = useState<number | null>(channel.defaultChatbotId);
  const [departmentId, setDepartmentId] = useState<number | null>(channel.defaultDepartmentId);
  const [accessGroupId, setAccessGroupId] = useState<number | null>(channel.defaultAccessGroupId);
  // 0 = "Não fechar" (representa null no banco).
  const [autoCloseMinutes, setAutoCloseMinutes] = useState<number>(
    channel.autoCloseInactiveMinutes ?? 0,
  );
  // 0 = "Não travar" (representa null no banco).
  const [offlineToPendingMinutes, setOfflineToPendingMinutes] = useState<number>(
    channel.offlineToPendingMinutes ?? 0,
  );
  const [autoAssignMode, setAutoAssignMode] = useState<AutoAssignMode>(
    channel.autoAssignMode ?? 'off',
  );
  const [hidePendingContent, setHidePendingContent] = useState<boolean>(
    channel.hidePendingContent ?? false,
  );
  const [sendClosingMessageOnBulk, setSendClosingMessageOnBulk] = useState<boolean>(
    channel.sendClosingMessageOnBulk ?? false,
  );
  // Agrupador de texto: 0 = desligado (o banco guarda NULL).
  const [textBatchSeconds, setTextBatchSeconds] = useState<number>(channel.textBatchSeconds ?? 0);
  // O que está escrito no campo, separado do número: apagar tudo pra digitar
  // outro valor não pode zerar o agrupador no meio da digitação (os campos
  // dependentes sumiriam e voltariam a cada tecla). Encaixa na faixa ao sair.
  const [textBatchSecondsText, setTextBatchSecondsText] = useState<string>(
    String(channel.textBatchSeconds ?? 0),
  );
  const [aiRewriteEnabled, setAiRewriteEnabled] = useState<boolean>(
    channel.aiRewriteEnabled ?? false,
  );
  // Modo do agrupador + teto (só valem com o agrupador ligado).
  const [textBatchMode, setTextBatchMode] = useState<'fixed' | 'adaptive' | 'smart'>(
    channel.textBatchMode ?? 'adaptive',
  );
  // Separação entre os textos do pacote (só vale com o agrupador ligado).
  const [textBatchSeparator, setTextBatchSeparator] = useState<'line' | 'blank_line' | 'space'>(
    channel.textBatchSeparator ?? 'line',
  );
  const [textBatchMaxSeconds, setTextBatchMaxSeconds] = useState<number>(
    channel.textBatchMaxSeconds ?? 40,
  );
  const [textBatchMaxSecondsText, setTextBatchMaxSecondsText] = useState<string>(
    String(channel.textBatchMaxSeconds ?? 40),
  );
  const [showBillingBadge, setShowBillingBadge] = useState<boolean>(
    channel.showBillingBadge ?? false,
  );
  /** Exigir motivo pra encerrar atendimento neste canal. */
  const [requireClosingReason, setRequireClosingReason] = useState<boolean>(
    channel.requireClosingReason ?? true,
  );
  /** Atendente pode editar msg enviada (≤15min). Nasce desligado. */
  const [allowMessageEdit, setAllowMessageEdit] = useState<boolean>(
    channel.allowMessageEdit ?? false,
  );
  const [error, setError] = useState<string | null>(null);
  // Selo de mensagem paga só faz sentido em canal oficial (Cloud/coexistência).
  const isMetaOfficial = channel.channelType === 'meta_official';
  // Edição de msg enviada só existe no QR — a Cloud API da Meta não tem.
  const isQr = channel.channelType === 'qr_code';

  const chatbotsQ = useQuery({ queryKey: ['chatbots'], queryFn: listChatbots });
  const deptsQ = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const groupsQ = useQuery({ queryKey: ['access-groups'], queryFn: listAccessGroups });

  const mutation = useMutation({
    mutationFn: () => {
      const trimmed = name.trim();
      const input: {
        display_name?: string;
        default_chatbot_id?: number | null;
        default_department_id?: number | null;
        default_access_group_id?: number | null;
        auto_close_inactive_minutes?: number | null;
        offline_to_pending_minutes?: number | null;
        auto_assign_mode?: AutoAssignMode;
        hide_pending_content?: boolean;
        send_closing_message_on_bulk?: boolean;
        show_billing_badge?: boolean;
        text_batch_seconds?: number | null;
        text_batch_mode?: 'fixed' | 'adaptive' | 'smart';
        text_batch_separator?: 'line' | 'blank_line' | 'space';
        text_batch_max_seconds?: number;
        ai_rewrite_enabled?: boolean;
        require_closing_reason?: boolean;
        allow_message_edit?: boolean;
      } = {};
      if (trimmed !== (channel.displayName ?? '')) input.display_name = trimmed;
      if (chatbotId !== channel.defaultChatbotId) input.default_chatbot_id = chatbotId;
      if (departmentId !== channel.defaultDepartmentId) input.default_department_id = departmentId;
      if (accessGroupId !== channel.defaultAccessGroupId)
        input.default_access_group_id = accessGroupId;
      if (autoCloseMinutes !== (channel.autoCloseInactiveMinutes ?? 0))
        input.auto_close_inactive_minutes = autoCloseMinutes === 0 ? null : autoCloseMinutes;
      if (offlineToPendingMinutes !== (channel.offlineToPendingMinutes ?? 0))
        input.offline_to_pending_minutes =
          offlineToPendingMinutes === 0 ? null : offlineToPendingMinutes;
      if (autoAssignMode !== (channel.autoAssignMode ?? 'off'))
        input.auto_assign_mode = autoAssignMode;
      if (hidePendingContent !== (channel.hidePendingContent ?? false))
        input.hide_pending_content = hidePendingContent;
      if (sendClosingMessageOnBulk !== (channel.sendClosingMessageOnBulk ?? false))
        input.send_closing_message_on_bulk = sendClosingMessageOnBulk;
      if (showBillingBadge !== (channel.showBillingBadge ?? false))
        input.show_billing_badge = showBillingBadge;
      if (textBatchSeconds !== (channel.textBatchSeconds ?? 0))
        input.text_batch_seconds = textBatchSeconds === 0 ? null : textBatchSeconds;
      if (aiRewriteEnabled !== (channel.aiRewriteEnabled ?? false))
        input.ai_rewrite_enabled = aiRewriteEnabled;
      if (textBatchMode !== (channel.textBatchMode ?? 'adaptive'))
        input.text_batch_mode = textBatchMode;
      if (textBatchSeparator !== (channel.textBatchSeparator ?? 'line'))
        input.text_batch_separator = textBatchSeparator;
      if (textBatchMaxSeconds !== (channel.textBatchMaxSeconds ?? 40))
        input.text_batch_max_seconds = textBatchMaxSeconds;
      if (requireClosingReason !== (channel.requireClosingReason ?? true))
        input.require_closing_reason = requireClosingReason;
      if (allowMessageEdit !== (channel.allowMessageEdit ?? false))
        input.allow_message_edit = allowMessageEdit;
      return updateChannelSettings(channel.id, input);
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      // Vincular/desvincular chatbot aqui muda a coluna "Canal vinculado" da
      // lista de chatbots — invalida pra refletir sem F5.
      void qc.invalidateQueries({ queryKey: ['chatbots'] });
      // Conversas e lista de atendimentos carregam as regras do canal (editar
      // msg, exigir motivo, fila cega…) — sem isto a conversa aberta seguia com
      // a regra antiga até um Ctrl+Shift+R. Outras abas/atendentes pegam no
      // próximo refetch do ticket (trocar de conversa / voltar pra aba).
      void qc.invalidateQueries({ queryKey: ['ticket'] });
      void qc.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Configurações salvas');
      // Canal 360dialog: todo save reaponta o webhook pra cá. Os dois casos
      // que a pessoa PRECISA ver são "tirei de outro sistema" (porque corta o
      // recebimento lá) e "não consegui" (porque o canal segue mudo).
      const w = r?.webhook;
      if (w?.estado === 'trazido_de_volta') {
        toast.warning('Webhook trazido de volta pro ChatPlus', {
          description: `Estava apontado para ${w.de} — aquele sistema para de receber este número agora.`,
          duration: 12_000,
        });
      } else if (w?.estado === 'falhou') {
        toast.error('Não consegui confirmar o webhook no 360dialog', {
          description: 'As configurações foram salvas, mas o recebimento pode continuar indo pra outro lugar.',
          duration: 12_000,
        });
      }
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.problem.title : 'Erro ao salvar');
    },
  });

  const trimmed = name.trim();
  const nameValid = trimmed.length > 0 && trimmed.length <= 60;
  const dirty =
    trimmed !== (channel.displayName ?? '') ||
    chatbotId !== channel.defaultChatbotId ||
    departmentId !== channel.defaultDepartmentId ||
    accessGroupId !== channel.defaultAccessGroupId ||
    autoCloseMinutes !== (channel.autoCloseInactiveMinutes ?? 0) ||
    offlineToPendingMinutes !== (channel.offlineToPendingMinutes ?? 0) ||
    autoAssignMode !== (channel.autoAssignMode ?? 'off') ||
    hidePendingContent !== (channel.hidePendingContent ?? false) ||
    sendClosingMessageOnBulk !== (channel.sendClosingMessageOnBulk ?? false) ||
    showBillingBadge !== (channel.showBillingBadge ?? false) ||
    textBatchSeconds !== (channel.textBatchSeconds ?? 0) ||
    aiRewriteEnabled !== (channel.aiRewriteEnabled ?? false) ||
    textBatchMode !== (channel.textBatchMode ?? 'adaptive') ||
    textBatchSeparator !== (channel.textBatchSeparator ?? 'line') ||
    textBatchMaxSeconds !== (channel.textBatchMaxSeconds ?? 40) ||
    requireClosingReason !== (channel.requireClosingReason ?? true) ||
    allowMessageEdit !== (channel.allowMessageEdit ?? false);
  const submitting = mutation.isPending;
  // Fecha SÓ com Esc ou Cancelar — clique fora não fecha (pedido do dono:
  // um clique errado perdia tudo que foi mexido). Esc escuta a página toda,
  // não só o foco dentro do card; ignorado enquanto salva.
  useEscapeClose(!submitting, onClose);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-n-900/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-[800px] rounded-md border border-n-200 bg-n-0 p-6 shadow-lg">
        <h2 className="mb-1 font-bold text-[16px] text-n-900">Configurações do canal</h2>
        <p className="mb-4 text-[12px] text-n-500">
          Apelido interno do ChatPlus, chatbot e departamento padrão usados nos atendimentos desse
          canal.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!nameValid || !dirty || submitting) return;
            setError(null);
            mutation.mutate();
          }}
          className="space-y-4"
        >
          {/* 3 campos por linha (2 em tela estreita) pra não empilhar tudo verticalmente. */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="font-semibold text-[12px] text-n-700">Apelido</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                placeholder="WhatsApp Suporte"
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
              />
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="font-semibold text-[12px] text-n-700">
                Chatbot padrão <span className="font-normal text-n-500">(opcional)</span>
              </span>
              <select
                value={chatbotId ?? ''}
                onChange={(e) => setChatbotId(e.target.value ? Number(e.target.value) : null)}
                disabled={chatbotsQ.isLoading}
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:opacity-50"
              >
                <option value="">— Sem chatbot —</option>
                {(chatbotsQ.data ?? [])
                  .filter((b) => b.status === 'active')
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
              </select>
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="font-semibold text-[12px] text-n-700">
                Departamento padrão <span className="font-normal text-n-500">(opcional)</span>
              </span>
              <select
                value={departmentId ?? ''}
                onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : null)}
                disabled={deptsQ.isLoading}
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:opacity-50"
              >
                <option value="">— Sem departamento —</option>
                {(deptsQ.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>

            {(groupsQ.data?.length ?? 0) > 0 && (
              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="font-semibold text-[12px] text-n-700">
                  Grupo de acesso <span className="font-normal text-n-500">(opcional)</span>
                </span>
                <select
                  value={accessGroupId ?? ''}
                  onChange={(e) => setAccessGroupId(e.target.value ? Number(e.target.value) : null)}
                  disabled={groupsQ.isLoading}
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:opacity-50"
                >
                  <option value="">— Sem grupo —</option>
                  {(groupsQ.data ?? []).map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="font-semibold text-[12px] text-n-700">
                Encerrar por inatividade <span className="font-normal text-n-500">(opcional)</span>
              </span>
              <select
                value={autoCloseMinutes}
                onChange={(e) => setAutoCloseMinutes(Number(e.target.value))}
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
              >
                {AUTO_CLOSE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span
                className="font-semibold text-[12px] text-n-700"
                title="Quando o atendente fica desconectado (sem nenhuma aba do painel nem app aberto) por esse tempo, os atendimentos ativos dele neste canal saem dele. Com distribuição automática, vão para quem estiver online no departamento, pela regra da distribuição. Se a distribuição for manual ou ninguém estiver online, voltam para Pendentes. Vale 24h."
              >
                Travar atendimento <span className="font-normal text-n-500">(opcional)</span>
              </span>
              <select
                value={offlineToPendingMinutes}
                onChange={(e) => setOfflineToPendingMinutes(Number(e.target.value))}
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
              >
                {opcoesComValorAtual(
                  OFFLINE_TO_PENDING_OPTIONS,
                  channel.offlineToPendingMinutes,
                ).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="font-semibold text-[12px] text-n-700">
                Estratégia de distribuição
              </span>
              <select
                value={autoAssignMode}
                onChange={(e) => setAutoAssignMode(e.target.value as AutoAssignMode)}
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
              >
                {AUTO_ASSIGN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span
                className="font-semibold text-[12px] text-n-700"
                title="Atendentes não-admin não veem a prévia nem a conversa de pendentes deste canal até aceitar. Admin vê tudo."
              >
                Ocultar conversa até aceitar
              </span>
              <select
                value={hidePendingContent ? 'sim' : 'nao'}
                onChange={(e) => setHidePendingContent(e.target.value === 'sim')}
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
              >
                <option value="nao">Não</option>
                <option value="sim">Sim</option>
              </select>
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span
                className="font-semibold text-[12px] text-n-700"
                title="Quando ligado, o fechamento em massa também envia a mensagem configurada no motivo de fechamento. No Cloud API respeita a janela 24h (fora dela, não envia)."
              >
                Mensagem de fechamento em massa
              </span>
              <select
                value={sendClosingMessageOnBulk ? 'sim' : 'nao'}
                onChange={(e) => setSendClosingMessageOnBulk(e.target.value === 'sim')}
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
              >
                <option value="nao">Não</option>
                <option value="sim">Sim</option>
              </select>
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span
                className="font-semibold text-[12px] text-n-700"
                title="Sim: o atendente escolhe um motivo pra encerrar (como sempre foi). Não: pode encerrar direto — o atendimento fica registrado como 'Não especificado' no relatório."
              >
                Exigir motivo de encerramento
              </span>
              <select
                value={requireClosingReason ? 'sim' : 'nao'}
                onChange={(e) => setRequireClosingReason(e.target.value === 'sim')}
                className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
              >
                <option value="sim">Sim</option>
                <option value="nao">Não</option>
              </select>
            </label>

            {isQr && (
              <label className="flex min-w-0 flex-col gap-1.5">
                <span
                  className="font-semibold text-[12px] text-n-700"
                  title="Sim: o atendente pode corrigir o texto de uma mensagem que enviou, até 15 minutos depois (o cliente vê 'Editada'). Só quem enviou ou um admin edita."
                >
                  Permitir edição de mensagem
                </span>
                <select
                  value={allowMessageEdit ? 'sim' : 'nao'}
                  onChange={(e) => setAllowMessageEdit(e.target.value === 'sim')}
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
                >
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </select>
              </label>
            )}

            {isMetaOfficial && (
              <label className="flex min-w-0 flex-col gap-1.5">
                <span
                  className="font-semibold text-[12px] text-n-700"
                  title="Marca no balão as mensagens de saída pela API oficial da Meta (cobradas a partir de 01/10) com um acento âmbar + 'Cobrada'."
                >
                  Selo de mensagem paga
                </span>
                <select
                  value={showBillingBadge ? 'sim' : 'nao'}
                  onChange={(e) => setShowBillingBadge(e.target.value === 'sim')}
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
                >
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </select>
              </label>
            )}

            {isMetaOfficial && (
              <label className="flex min-w-0 flex-col gap-1.5">
                <span
                  className="font-semibold text-[12px] text-n-700"
                  title="Junta os textos que o atendente manda em sequência numa mensagem só: em vez de 3 mensagens (e 3 cobranças), sai 1. Vale só pro texto digitado no atendimento — mídia, template e nota interna saem na hora. Desabilitado: não agrupa. Fixo: o relógio começa no 1º texto e não reinicia — o que chegar dentro do tempo vai junto. Adaptativo: cada texto novo reinicia a espera. Inteligente: como o adaptativo, e ainda fica parado enquanto o atendente tem texto na caixa — retoma quando ela esvazia."
                >
                  Modo do agrupador
                </span>
                {/* Desabilitado = 0 segundos (o banco guarda NULL). Ligar um modo
                    traz os campos do agrupador na linha de baixo. */}
                <select
                  value={textBatchSeconds > 0 ? textBatchMode : 'off'}
                  onChange={(e) => {
                    const v = e.target.value as 'off' | 'fixed' | 'adaptive' | 'smart';
                    if (v === 'off') {
                      setTextBatchSeconds(0);
                      setTextBatchSecondsText('0');
                      return;
                    }
                    setTextBatchMode(v);
                    if (textBatchSeconds === 0) {
                      setTextBatchSeconds(10);
                      setTextBatchSecondsText('10');
                    }
                  }}
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
                >
                  <option value="off">Desabilitado</option>
                  <option value="fixed">Fixo — o relógio não reinicia</option>
                  <option value="adaptive">Adaptativo — reinicia a cada texto</option>
                  <option value="smart">Inteligente — para enquanto digita</option>
                </select>
              </label>
            )}

            {isMetaOfficial && textBatchSeconds > 0 && (
              // Sem col-start: os campos do agrupador ocupam o próximo espaço
              // livre do grid — com ou sem "Grupo de acesso" antes, não sobra
              // buraco do lado do modo.
              <label className="flex min-w-0 flex-col gap-1.5">
                <span
                  className="font-semibold text-[12px] text-n-700"
                  title="Quanto tempo o agrupador espera por mais texto antes de enviar o pacote."
                >
                  Agrupar mensagens de texto (segundos)
                </span>
                <input
                  type="number"
                  min={1}
                  max={60}
                  step={1}
                  value={textBatchSecondsText}
                  onChange={(e) => {
                    setTextBatchSecondsText(e.target.value);
                    const n = Number(e.target.value);
                    // 0 desligaria o agrupador no meio da digitação — desligar é no modo.
                    if (e.target.value !== '' && Number.isFinite(n) && n > 0)
                      setTextBatchSeconds(n);
                  }}
                  // Fora da faixa (ou vazio): encaixa em 1–60 ao sair do campo —
                  // a API recusaria de qualquer jeito.
                  onBlur={() => {
                    const n = Math.min(60, Math.max(1, Math.round(textBatchSeconds)));
                    setTextBatchSeconds(n);
                    setTextBatchSecondsText(String(n));
                  }}
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
                />
              </label>
            )}

            {isMetaOfficial && textBatchSeconds > 0 && textBatchMode !== 'fixed' && (
              <label className="flex min-w-0 flex-col gap-1.5">
                <span
                  className="font-semibold text-[12px] text-n-700"
                  title="Máximo que o pacote espera desde o 1º texto, mesmo que o atendente continue digitando ou segurando. É a garantia de que o cliente não fica no vácuo com texto esquecido na caixa."
                >
                  Tempo máximo de espera (segundos)
                </span>
                <input
                  type="number"
                  min={10}
                  max={600}
                  step={5}
                  value={textBatchMaxSecondsText}
                  onChange={(e) => {
                    setTextBatchMaxSecondsText(e.target.value);
                    const n = Number(e.target.value);
                    if (e.target.value !== '' && Number.isFinite(n)) setTextBatchMaxSeconds(n);
                  }}
                  // Fora da faixa (ou vazio): encaixa em 10–600 ao sair do campo —
                  // a API recusaria de qualquer jeito.
                  onBlur={() => {
                    const n = Math.min(600, Math.max(10, Math.round(textBatchMaxSeconds || 40)));
                    setTextBatchMaxSeconds(n);
                    setTextBatchMaxSecondsText(String(n));
                  }}
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
                />
              </label>
            )}

            {isMetaOfficial && textBatchSeconds > 0 && (
              <label className="flex min-w-0 flex-col gap-1.5">
                <span
                  className="font-semibold text-[12px] text-n-700"
                  title="Como os textos do pacote ficam separados na mensagem que o cliente recebe. Quebra de linha: um embaixo do outro (como sempre foi). Linha em branco: um embaixo do outro, com uma linha vazia entre eles. Mesma linha: tudo seguido, separado por espaço."
                >
                  Separação entre as mensagens
                </span>
                <select
                  value={textBatchSeparator}
                  onChange={(e) =>
                    setTextBatchSeparator(e.target.value as 'line' | 'blank_line' | 'space')
                  }
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
                >
                  <option value="line">Quebra de linha (padrão)</option>
                  <option value="blank_line">Linha em branco</option>
                  <option value="space">Mesma linha</option>
                </select>
              </label>
            )}

            {/* Só existe com o agrupador ligado: sem pacote não há o que melhorar. */}
            {isMetaOfficial && textBatchSeconds > 0 && (
              <label className="flex min-w-0 flex-col gap-1.5">
                <span
                  className="font-semibold text-[12px] text-n-700"
                  title="Quando o agrupador junta 2 ou mais textos, a IA reescreve tudo numa mensagem bem escrita antes de sair: 'oi' / 'boa tarde' / 'como posso ajudar' chega como 'Olá, boa tarde! Como posso te ajudar hoje?'. Se a IA falhar ou demorar, a mensagem sai exatamente como foi digitada."
                >
                  Melhorar texto com IA
                </span>
                <select
                  value={aiRewriteEnabled ? 'sim' : 'nao'}
                  onChange={(e) => setAiRewriteEnabled(e.target.value === 'sim')}
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
                >
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </select>
              </label>
            )}
          </div>

          {/* Seção "App Digitador" (DigitadorSection.tsx) retirada: o Digitador
              virou legado em 13/out/2026 — vide DIGITADOR_ATIVO na API. */}

          {error && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-n-300 bg-n-0 px-4 py-2 font-semibold text-[12px] text-n-700 hover:bg-n-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || !nameValid || !dirty}
              className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 font-bold text-[12px] text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {submitting && <Loader2 size={12} className="animate-spin" />}
              {submitting ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
